import { AxiosRequestConfig } from "axios";
import {
  CORE_API,
  CUR8_SERVER,
  NOTIFICATION_SERVER,
} from "gatsby-env-variables";
import { getViewAsSessionId } from "./viewAsSession";
import { isViewAsSessionId } from "./viewAsStorage";

export const VIEW_AS_HEADER = "X-Impersonation-Session";

// The refresh call authenticates the staff member's own cookies and is refused with the header.
const HEADERLESS_PATHS = ["/auth/refresh-access-token"];

// Every first-party API: invest-api serves the session, the others refuse the header, none is called after it ends.
const CUR8_API_BASE_URLS: unknown[] = [
  CUR8_SERVER,
  CORE_API,
  NOTIFICATION_SERVER,
];

const apiOrigins = (): string[] =>
  CUR8_API_BASE_URLS.flatMap((host) => {
    if (typeof host !== "string" || host.length === 0) return [];
    try {
      return [new URL(host).origin];
    } catch {
      return [];
    }
  });

const resolveApiUrl = (config: AxiosRequestConfig): URL | null => {
  const origins = apiOrigins();
  if (origins.length === 0 || !config.url) return null;

  const base =
    config.baseURL ??
    (typeof window === "undefined" ? undefined : window.location.origin);

  try {
    const url = new URL(config.url, base);
    return origins.includes(url.origin) ? url : null;
  } catch {
    return null;
  }
};

export const isCur8ApiRequest = (config: AxiosRequestConfig): boolean =>
  resolveApiUrl(config) !== null;

export const shouldSendViewAsHeader = (config: AxiosRequestConfig): boolean => {
  const url = resolveApiUrl(config);

  return url !== null && !HEADERLESS_PATHS.includes(url.pathname);
};

export const applyViewAsHeader = <T extends AxiosRequestConfig>(
  config: T
): T => {
  const sessionId = getViewAsSessionId();

  if (!sessionId || !shouldSendViewAsHeader(config)) return config;

  config.headers = config.headers ?? {};
  config.headers[VIEW_AS_HEADER] = sessionId;

  return config;
};

export const readRequestViewAsSessionId = (
  config?: AxiosRequestConfig
): string | null => {
  const headers = config?.headers as Record<string, unknown> | undefined;
  if (!headers) return null;

  const name = Object.keys(headers).find(
    (key) => key.toLowerCase() === VIEW_AS_HEADER.toLowerCase()
  );
  const value = name ? headers[name] : undefined;

  return isViewAsSessionId(value) ? value : null;
};
